import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import CommonAvatar from "@/modules/common/components/ui-kit/common-avatar";
import { SxProps } from "@mui/material";
import Color from "color";
import { Rank } from "@/modules/user/types/index.v2";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { mapRatingAgentRank } from "@/modules/common/utils";

interface AssignedAgentDisplayProps {
  user?: { name?: string; color?: string; rank?: Rank } | null;
  fontSize?: any;
  label?: string;
  withRank?: boolean;
  agentNameSx?: SxProps;
  avatarSize?: number;
  isMappingRank?: boolean;
}

const AssignedAgentDisplay: React.FC<AssignedAgentDisplayProps> = ({
  user,
  fontSize,
  label,
  withRank = false,
  agentNameSx,
  avatarSize = 30,
  isMappingRank = false,
}) => {
  const theme = useTheme();
  const defaultFontSize =
    fontSize || theme.nawy.typography.english.variants.body.lg.regular;

  // Safely validate colors, since Color() throws on invalid input
  const isColorValid = (value?: string | null): value is string => {
    if (!value) return false;
    try {
      Color(value);
      return true;
    } catch {
      return false;
    }
  };

  const displayName =
    user?.name && user.name.trim().length > 0 ? user.name : "Unassigned";

  const nameColor = (() => {
    if (!isColorValid(user?.color)) return theme.palette.text.primary;
    try {
      return new Color(user!.color!).darken(0.5).toString();
    } catch {
      return theme.palette.text.primary;
    }
  })();

  const avatarColor = isColorValid(user?.color) ? user!.color! : undefined;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {/* Avatar */}
      <CommonAvatar
        name={displayName}
        color={avatarColor}
        size={avatarSize}
        fontSize={defaultFontSize}
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          gap: "4px",
        }}
      >
        {label && (
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: 500,
              color: theme.nawy.colors.semantic.text.secondary,
              lineHeight: 1.2,
            }}
          >
            {label}
          </Typography>
        )}
        {withRank && (
          <Typography
            sx={{
              ...nawyTheme.nawy.typography.english.variants.body.xs.regular,
              color: theme.nawy.colors.semantic.text.secondary,
              display: "block",
            }}
          >
            {isMappingRank
              ? mapRatingAgentRank(user?.rank?.title || "")
              : user?.rank?.title || "No rank"}
          </Typography>
        )}
        <Typography
          sx={{
            ...defaultFontSize,
            color: nameColor,
            fontWeight: "500",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.2,
            ...agentNameSx,
          }}
        >
          {displayName}
        </Typography>
      </Box>
    </Box>
  );
};

export default AssignedAgentDisplay;
